import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const navLinks = {
  user: [
    { to: '/user/dashboard',        label: 'Dashboard',       icon: '▦' },
    { type: 'section', label: 'New Request' },
    { to: '/user/request/material', label: 'Material Master', icon: '◈' },
    { to: '/user/request/vendor',   label: 'Vendor Master',   icon: '◉' },
    { to: '/user/my-requests',      label: 'My Requests',     icon: '≡' },
    { to: '/user/drafts',           label: 'Drafts',          icon: '✎' },
  ],
  checker: [
    { to: '/checker/dashboard',          label: 'Dashboard',          icon: '▦' },
    { type: 'section', label: 'Review' },
    { to: '/checker/review/material',    label: 'Material Requests',  icon: '◈' },
    { to: '/checker/review/vendor',      label: 'Vendor Requests',    icon: '◉' },
  ],
  approver: [
    { to: '/approver/dashboard',         label: 'Dashboard',          icon: '▦' },
    { type: 'section', label: 'Approvals' },
    { to: '/approver/approve/material',  label: 'Material Approvals', icon: '◈' },
    { to: '/approver/approve/vendor',    label: 'Vendor Approvals',   icon: '◉' },
  ],
  it_support: [
    { to: '/it/dashboard',               label: 'Dashboard',          icon: '▦' },
    { type: 'section', label: 'Incoming' },
    { to: '/it/incoming/material',       label: 'Material Requests',  icon: '◈' },
    { to: '/it/incoming/vendor',         label: 'Vendor Requests',    icon: '◉' },
  ],
  admin: [
    { to: '/admin/dashboard', label: 'Dashboard',    icon: '▦' },
    { to: '/admin/users',     label: 'Manage Users', icon: '⊙' },
    { type: 'section', label: 'Requests' },
    { to: '/admin/requests/material', label: 'Material Requests', icon: '◈' },
    { to: '/admin/requests/vendor',   label: 'Vendor Requests',   icon: '◉' },
  ],
};

const roleLabel = {
  user: 'User', checker: 'Checker', approver: 'Approver',
  it_support: 'IT Support', admin: 'Administrator',
};

const roleGradient = {
  user:      'linear-gradient(135deg,#4f6ef7,#1A2C80)',
  checker:   'linear-gradient(135deg,#0891b2,#0e7490)',
  approver:  'linear-gradient(135deg,#f4a261,#E28141)',
  it_support:'linear-gradient(135deg,#e87a3a,#D95A1A)',
  admin:     'linear-gradient(135deg,#c45a80,#AC4168)',
};

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [draftCount, setDraftCount] = useState(0);
  const handleLogout = () => { logout(); navigate('/login'); };

  useEffect(() => {
    if (user?.role === 'user')
      api.get('/drafts').then(r => setDraftCount(r.data.length)).catch(() => {});
  }, [user, location.pathname]);

  return (
    <aside style={S.sidebar}>
      {/* Portal name */}
      <div style={S.logoArea}>
        <div style={S.portalName}>Master Data Portal</div>
      </div>

      {/* Nav */}
      <nav style={S.nav}>
        {user && navLinks[user.role]?.map((l, i) => {
          if (l.type === 'section') return (
            <div key={i} style={S.section}>{l.label}</div>
          );
          const active = location.pathname === l.to;
          return (
            <Link key={l.to} to={l.to} style={{ ...S.link, ...(active ? S.linkActive : {}) }}>
              <span style={{ ...S.linkIcon, ...(active ? S.linkIconActive : {}) }}>{l.icon}</span>
              <span style={S.linkLabel}>{l.label}</span>
              {l.to === '/user/drafts' && draftCount > 0 && (
                <span style={S.draftBadge}>{draftCount}</span>
              )}
              {active && <span style={S.activePill} />}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div style={S.bottom}>
        <div style={S.sep} />
        {user && (
          <div style={S.userCard}>
            <div style={{ ...S.avatar, background: roleGradient[user.role] || roleGradient.user }}>
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div style={S.userInfo}>
              <div style={S.userName}>{user.name}</div>
              <div style={S.userRole}>{roleLabel[user.role] || user.role}</div>
            </div>
          </div>
        )}
        <button onClick={handleLogout} style={S.logoutBtn}>
          <span style={S.logoutIcon}>↩</span>
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
};

const S = {
  sidebar: {
    width: 248, minHeight: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 200,
    background: 'linear-gradient(180deg, #060d3a 0%, #0d1a6e 40%, #111f7a 70%, #0a1560 100%)',
    display: 'flex', flexDirection: 'column',
    boxShadow: '4px 0 32px rgba(6,13,58,0.45)',
    borderRight: '1px solid rgba(255,255,255,0.04)',
  },
  logoArea: {
    padding: '20px 20px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  portalName: {
    color: '#F7D506', fontSize: 10, fontWeight: 800,
    letterSpacing: 2.5, textTransform: 'uppercase', textAlign: 'center',
  },
  userCard: {
    margin: '0 0 10px', padding: '10px 12px',
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10,
    border: '1px solid rgba(255,255,255,0.08)',
    backdropFilter: 'blur(8px)',
  },
  avatar: {
    width: 38, height: 38, borderRadius: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: 16, color: '#fff', flexShrink: 0,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  userInfo: { overflow: 'hidden' },
  userName: { color: '#fff', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userRole: { color: '#a5b4fc', fontSize: 11, marginTop: 2, fontWeight: 500 },
  nav: { flex: 1, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 2 },
  section: {
    color: 'rgba(165,180,252,0.5)', fontSize: 9.5, fontWeight: 800,
    letterSpacing: 1.8, textTransform: 'uppercase',
    padding: '14px 12px 6px',
  },
  link: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px', borderRadius: 10,
    color: 'rgba(255,255,255,0.55)', textDecoration: 'none',
    fontSize: 13.5, fontWeight: 500, position: 'relative',
    transition: 'all 0.15s',
  },
  linkActive: {
    background: 'linear-gradient(135deg,rgba(79,110,247,0.25),rgba(79,110,247,0.1))',
    color: '#fff', fontWeight: 700,
    border: '1px solid rgba(79,110,247,0.3)',
    boxShadow: '0 2px 12px rgba(79,110,247,0.15)',
  },
  linkIcon: {
    width: 30, height: 30, borderRadius: 8,
    background: 'rgba(255,255,255,0.06)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, flexShrink: 0,
  },
  linkIconActive: {
    background: 'linear-gradient(135deg,#4f6ef7,#1A2C80)',
    color: '#fff',
    boxShadow: '0 2px 8px rgba(79,110,247,0.4)',
  },
  linkLabel: { flex: 1 },
  activePill: {
    width: 4, height: 18, borderRadius: 2,
    background: 'linear-gradient(180deg,#4f6ef7,#a5b4fc)',
    flexShrink: 0,
  },
  draftBadge: {
    minWidth: 18, height: 18, borderRadius: 9, padding: '0 5px',
    background: '#f59e0b', color: '#fff',
    fontSize: 10, fontWeight: 800, textAlign: 'center', lineHeight: '18px',
    flexShrink: 0,
  },
  bottom: { padding: '0 10px 24px' },
  sep: { height: 1, background: 'rgba(255,255,255,0.07)', margin: '0 4px 14px' },
  logoutBtn: {
    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(239,68,68,0.2)',
    background: 'rgba(239,68,68,0.08)', color: '#fca5a5',
    fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s',
  },
  logoutIcon: { fontSize: 16 },
};

export default Sidebar;
